import { useState, useEffect, useCallback, useRef } from "react";
import { MapPin, X, Check, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";

/**
 * Persistent Location Pill — a small floating badge that always shows the
 * client's chosen clinic ("📍 Booking at Ottawa · Change") and follows
 * them across every page. On first visit, or when tapped, it opens a
 * modal to pick between Ottawa, Kanata, and Carleton Place.
 *
 * The chosen location is stored in localStorage under
 * `preferred_clinic_location` so the whole site (booking flow, search,
 * add-to-cart) can read it.
 *
 * Read from anywhere:
 *   import { getPreferredLocation } from "@/components/PersistentLocationPill";
 *   const loc = getPreferredLocation();  // "Ottawa" | "Kanata" | "Carleton Place" | null
 */

const STORAGE_KEY = "preferred_clinic_location";

const LOCATIONS = [
  {
    id: "Ottawa",
    en: { name: "Ottawa", address: "1390 Prince of Wales Dr, Main Floor", note: "Main flagship · Karine's home clinic" },
    fr: { name: "Ottawa", address: "1390 Prince of Wales, Rez-de-chaussée", note: "Clinique principale de Karine" },
  },
  {
    id: "Kanata",
    en: { name: "Kanata", address: "320 March Rd, Clinic 603, 6th Fl", note: "West-end convenience" },
    fr: { name: "Kanata", address: "320 March Rd, Clinique 603, 6e ét.", note: "Ouest — pratique" },
  },
  {
    id: "Carleton Place",
    en: { name: "Carleton Place", address: "163 Bridge St, Downtown", note: "Cozy small-town clinic" },
    fr: { name: "Carleton Place", address: "163 Bridge St, Centre-ville", note: "Petit village charmant" },
  },
];

// Read helper for other components
export const getPreferredLocation = () => {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};

// Fire a window event when location changes so listeners can react
const LOCATION_EVENT = "preferred-location-change";

export const setPreferredLocation = (locationId) => {
  try {
    if (locationId) localStorage.setItem(STORAGE_KEY, locationId);
    else localStorage.removeItem(STORAGE_KEY);
    window.dispatchEvent(new CustomEvent(LOCATION_EVENT, { detail: locationId }));
  } catch {
    /* ignore quota / privacy mode errors */
  }
};

const PersistentLocationPill = () => {
  const { language } = useLanguage();
  const isFr = language === "fr";
  const [location, setLocationState] = useState(() => getPreferredLocation());
  const [modalOpen, setModalOpen] = useState(false);
  // Sep 22 2026 · Karine ask — hide this pill on `/crown-reserve-plan*`
  // routes so it never covers the "BOOK THIS PLAN" CTA. We can't use
  // React Router's `useLocation` here (this component is rendered
  // outside `<BrowserRouter>`), so we listen for popstate + patch
  // pushState/replaceState to keep `pathname` fresh across SPA nav.
  const [pathname, setPathname] = useState(
    typeof window !== "undefined" ? window.location.pathname : "",
  );
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const sync = () => setPathname(window.location.pathname);
    const origPush = window.history.pushState;
    const origReplace = window.history.replaceState;
    window.history.pushState = function (...args) {
      origPush.apply(this, args);
      sync();
    };
    window.history.replaceState = function (...args) {
      origReplace.apply(this, args);
      sync();
    };
    window.addEventListener("popstate", sync);
    return () => {
      window.history.pushState = origPush;
      window.history.replaceState = origReplace;
      window.removeEventListener("popstate", sync);
    };
  }, []);
  // Iter 455 · Feb 2026 · Karine ask — hide the pill + suppress the
  // 6-second first-visit nudge toast whenever the booking modal is
  // open. Prevents the pill and the "Pick your preferred clinic"
  // toast from overlaying the mobile bottom-sheet booking flow.
  const [bookingModalOpen, setBookingModalOpen] = useState(false);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const check = () => {
      const el = document.querySelector('[data-testid="booking-flow-modal"]');
      setBookingModalOpen(!!el);
    };
    check(); // initial pass in case the modal is already mounted
    const observer = new MutationObserver(check);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  // Listen for external changes (e.g., booking flow setting a location)
  useEffect(() => {
    const handler = (e) => setLocationState(e.detail);
    window.addEventListener(LOCATION_EVENT, handler);
    return () => window.removeEventListener(LOCATION_EVENT, handler);
  }, []);

  // First-visit nudge — if the client has no saved location AND hasn't
  // seen the nudge before, gently surface it after 6 seconds so we
  // don't spring on brand-new visitors the moment they land.
  // Iter 455 · skip entirely while the booking modal is open (or opens
  // during the 6s window) so we never overlay the mobile bottom sheet.
  // Track the toast id so we can dismiss ONLY this nudge — not
  // unrelated toasts (staff Welcome Back, cart confirms, etc.).
  const nudgeToastIdRef = useRef(null);

  useEffect(() => {
    if (bookingModalOpen || (pathname || "") === "/" || (pathname || "").startsWith("/account") || (pathname || "").startsWith("/book/menu")) return undefined;
    try {
      const hasSeenNudge = localStorage.getItem("has_seen_location_nudge");
      const hasLocation = localStorage.getItem(STORAGE_KEY);
      if (hasSeenNudge || hasLocation) return;
      const timer = setTimeout(() => {
        // Re-check right before firing — the booking modal may have
        // opened during the 6s delay.
        if (document.querySelector('[data-testid="booking-flow-modal"]')) return;
        localStorage.setItem("has_seen_location_nudge", "1");
        nudgeToastIdRef.current = toast(
          isFr
            ? "Choisissez votre clinique pour une réservation plus rapide"
            : "Pick your preferred clinic for faster booking",
          {
            description: isFr
              ? "Nous mémoriserons votre choix — Ottawa, Kanata ou Carleton Place."
              : "We'll remember it — Ottawa, Kanata or Carleton Place.",
            duration: 8000,
            action: {
              label: isFr ? "Choisir" : "Pick clinic",
              onClick: () => setModalOpen(true),
            },
          }
        );
      }, 6000);
      return () => clearTimeout(timer);
    } catch {
      /* localStorage unavailable */
    }
  }, [isFr, bookingModalOpen]);

  // Iter 455 · when the booking modal opens while THIS nudge toast is
  // visible, dismiss ONLY it so we don't clobber unrelated toasts.
  useEffect(() => {
    if (bookingModalOpen && nudgeToastIdRef.current !== null) {
      try { toast.dismiss(nudgeToastIdRef.current); } catch (_) { /* ignore */ }
      nudgeToastIdRef.current = null;
    }
  }, [bookingModalOpen]);

  const choose = useCallback((id) => {
    setPreferredLocation(id);
    setLocationState(id);
    setModalOpen(false);
  }, []);

  const currentCopy = LOCATIONS.find((l) => l.id === location);

  // Sep 22 2026 · Karine ask — the Crown Reserve plan pages have their
  // own explicit clinic-select pill inside each plan card + a big
  // "BOOK THIS PLAN" CTA at the bottom. The floating pill was hiding
  // that button on mobile, so we skip render on this route entirely.
  if ((pathname || "").startsWith("/crown-reserve-plan")) {
    return null;
  }

  // Feb 2026 · Karine ask — the "Booking at Ottawa · Change" pill was
  // overlapping the client search box on the management pages. Hide
  // it entirely on any admin / staff / head-office / kiosk surface —
  // those pages have their own explicit location selectors when
  // relevant, and the floating pill is only useful to public visitors.
  const _p = pathname || "";
  if (
    _p === "/" || // Oct 26 2026 · Karine: no floating pills on the main page
    _p.startsWith("/admin") ||
    _p.startsWith("/staff") ||
    _p.startsWith("/np/join") ||
    _p.startsWith("/book/menu") ||
    _p.startsWith("/t/") ||
    _p.startsWith("/md") ||
    _p.startsWith("/head-office") ||
    _p.startsWith("/checkin") ||
    _p.startsWith("/welcome") ||
    _p.startsWith("/signage") ||
    _p.startsWith("/tv") ||
    _p.startsWith("/lobby") ||
    _p.startsWith("/kiosk")
  ) {
    return null;
  }

  return (
    <>
      {/* Floating pill (bottom-left, above the Home FAB) — Iter 455:
          hidden while the booking modal is open so it never overlays
          the mobile bottom-sheet flow. */}
      {!bookingModalOpen && (
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className="fixed bottom-24 left-4 z-40 group inline-flex items-center gap-2 px-3 py-2 rounded-full bg-white/95 backdrop-blur-md border border-amber-200 shadow-lg hover:shadow-xl hover:border-amber-400 transition-all"
          data-testid="persistent-location-pill"
          aria-label={isFr ? "Changer d'emplacement" : "Change clinic location"}
        >
          <MapPin className="w-4 h-4 text-amber-600 group-hover:scale-110 transition-transform" strokeWidth={2.5} />
          <span className="text-xs font-semibold text-zinc-800">
            {currentCopy
              ? (isFr ? `Réservation à ${currentCopy.fr.name}` : `Booking at ${currentCopy.en.name}`)
              : (isFr ? "Choisir une clinique" : "Choose a clinic")
            }
          </span>
          <span className="text-[10px] font-semibold text-amber-700 group-hover:text-amber-900 flex items-center gap-0.5">
            {isFr ? "Changer" : "Change"}
            <ChevronDown className="w-3 h-3" />
          </span>
        </button>
      )}

      {/* Selection modal */}
      {modalOpen && (
        <div
          className="fixed inset-0 z-[10000] flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => setModalOpen(false)}
        >
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div
            className="relative w-full max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden animate-fade-in"
            onClick={(e) => e.stopPropagation()}
            data-testid="location-picker-modal"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100">
              <div>
                <h3 className="text-lg font-semibold text-zinc-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {isFr ? "Quelle clinique préférez-vous ?" : "Which clinic do you prefer?"}
                </h3>
                <p className="text-xs text-zinc-500 mt-0.5">
                  {isFr
                    ? "Nous garderons ce choix pour toutes vos réservations."
                    : "We'll remember this for all your bookings."}
                </p>
              </div>
              <button
                onClick={() => setModalOpen(false)}
                className="p-2 rounded-full hover:bg-zinc-100 transition-colors"
                aria-label={isFr ? "Fermer" : "Close"}
                data-testid="location-picker-close"
              >
                <X className="w-4 h-4 text-zinc-500" />
              </button>
            </div>

            <div className="p-3 space-y-2">
              {LOCATIONS.map((loc) => {
                const copy = isFr ? loc.fr : loc.en;
                const isActive = location === loc.id;
                return (
                  <button
                    key={loc.id}
                    onClick={() => choose(loc.id)}
                    className={`w-full text-left p-4 rounded-xl border transition-all ${
                      isActive
                        ? "border-amber-500 bg-amber-50 shadow-sm"
                        : "border-zinc-200 hover:border-amber-300 hover:bg-amber-50/40"
                    }`}
                    data-testid={`location-option-${loc.id.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                          isActive ? "bg-amber-500" : "bg-amber-100"
                        }`}>
                          <MapPin className={`w-5 h-5 ${isActive ? "text-white" : "text-amber-600"}`} strokeWidth={2.5} />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-zinc-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                            {copy.name}
                          </p>
                          <p className="text-xs text-zinc-600 mt-0.5">{copy.address}</p>
                          <p className="text-[11px] text-amber-700 italic mt-1">{copy.note}</p>
                        </div>
                      </div>
                      {isActive && (
                        <span className="flex items-center gap-1 text-xs font-semibold text-amber-700 whitespace-nowrap">
                          <Check className="w-3.5 h-3.5" strokeWidth={3} />
                          {isFr ? "Choisi" : "Chosen"}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="px-5 py-3 bg-zinc-50 border-t border-zinc-100 text-center">
              <p className="text-[11px] text-zinc-500">
                {isFr
                  ? "💡 Ce choix ne vous engage pas — vous pouvez le changer à tout moment"
                  : "💡 This doesn't lock you in — change it any time"}
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PersistentLocationPill;
